import { getLogger, type Logger } from "@expert/logging";
import { immer } from "zustand/middleware/immer";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";
import { useSessionStore } from "@/sdk/sessions/session.store";
import type { SummaryResolution } from "@/shared-types";

const logger = getLogger({
    module: "useSessionStore",
});

export type SessionSummary = string;

export interface SetSessionSummaryPayload {
    value: SessionSummary;
    autoGenerated?: boolean;
}

export interface SessionSummaryState {
    summary: SessionSummary;
    autogeneratedSummary: SessionSummary;
    resolutionStatus: SummaryResolution;
    /** true if resolution status form should be hidden from the summary modal */
    hideResolutionStatusForm: boolean;
    submitted: boolean;
    canAppend: boolean;
    customerEndedCall: boolean;
    setCanAppend: (value: boolean) => void;
    setSessionSummary: (payload: SetSessionSummaryPayload) => void;
    setSubject: (subject: string) => void;
    subject: string;
    setCategory: (category: string | undefined) => void;
    category: string | undefined;
    setSubcategory: (subcategory: string | undefined) => void;
    subcategory: string | undefined;
    appendSessionSummary: (fragment: SessionSummary) => void;
    /** reset state to default */
    resetSessionSummary: () => void;
    /**  change resolution status to predefined value. Optionally the form can be hidden from the summary modal when calling this */
    setResolved: (wasIssueResolved: SummaryResolution, options?: { hideResolutionStatusForm?: boolean }) => void;
    setSubmitted: (value: boolean) => void;
    setCustomerEndedCall: (value: boolean, logger: Logger) => void;
}

export const useSessionSummaryStore = createWithEqualityFn<SessionSummaryState>()(
    immer((set) => ({
        summary: "",
        autogeneratedSummary: "",
        resolutionStatus: null,
        subject: "",
        category: undefined,
        subcategory: undefined,
        hideResolutionStatusForm: false,
        submitted: false,
        canAppend: true,
        customerEndedCall: false,
        setCanAppend: (canAppend: boolean) => set({ canAppend }),
        setSessionSummary: ({ value, autoGenerated }: SetSessionSummaryPayload) => {
            set((state) => {
                state.summary = value;
                if (autoGenerated) state.autogeneratedSummary = value;
            });
        },
        setSubject: (subject) => {
            set((state) => {
                state.subject = subject;
            });
        },
        setCategory: (category) => {
            set((state) => {
                state.category = category;
            });
        },
        setSubcategory: (subcategory) => {
            set((state) => {
                state.subcategory = subcategory;
            });
        },
        setCustomerEndedCall: (callerHungUp: boolean, customLogger: Logger) => {
            customLogger.info({ customerEndedcall: callerHungUp }, "Customer ended call.");
            set({ customerEndedCall: callerHungUp });
        },
        appendSessionSummary: (fragment: SessionSummary) => {
            set((state) => {
                if (!state.canAppend) {
                    logger.warn("Cannot append to session summary, canAppend is false");
                    return;
                }

                const formatted = [state.summary, fragment].join(" ").trim();

                state.summary = formatted;
                state.autogeneratedSummary = formatted;
            });
        },
        resetSessionSummary: () =>
            set({
                summary: "",
                autogeneratedSummary: "",
                resolutionStatus: null,
                hideResolutionStatusForm: false,
                canAppend: true,
                subject: "",
                category: undefined,
                subcategory: undefined,
                submitted: false,
                customerEndedCall: false,
            }),
        setResolved: (wasIssueResolved: SummaryResolution, options = { hideResolutionStatusForm: false }) =>
            set({ resolutionStatus: wasIssueResolved, hideResolutionStatusForm: options.hideResolutionStatusForm }),
        setSubmitted: (submitted: boolean) => set({ submitted }),
    })),
    shallow,
);

useSessionStore.subscribe(({ sessions }, { sessions: prevSessions }) => {
    // if a task was added to any sessions then any previously declared summary resolution should be cleared
    const taskAdded = sessions.some((session, index) => session?.tasks.size > prevSessions[index]?.tasks.size);
    if (taskAdded) {
        useSessionSummaryStore.getState().setResolved(null);
    }
});
